/*
 * Component for creating a data table containing sortable column headers.
 *
 * @prop {object} tableAttributes (optional) - key/value pairs of your TABLE attributes, passed in
 *                                             exactly as you want them written
 * @prop {array|object} data - The data that's displayed in the individual table rows and used for
 *                             sorting, can be an array or an object if data is grouped
 * @prop {func} StandardRow - The function used to build and format the individual table rows when
 *                            table is NOT being virtualized
 * @prop {func} virtualRow - The function used to build and format individual table rows when table IS being virtualized
 * @prop {array} columns - Array of key/value pair objects for handling the column headers
 * @prop {array} hiddenColumns - Array of keys indicating which table header columns should not be rendered
 * @prop {string} tableCaption (optional) - Populates the CAPTION tag for the data table. Accepts:
 * @prop {bool} overrideVirtual (optional) - Table will automatically use virtual rendering if the number of
 *                                rows exceeds height of the table, use this prop to override that
 *                                behavior so table is not virtualized
 * @prop {int} itemSize - The height of a row, used to calculate whether the table should be virtualized
 *                        and in calculations when rendering the virtualized table
 * @prop {function} virtualKey - A function to determine the key applied to a row in the virtual table
 * @prop {function} standardKey - A function to determine the key applied to a row in the standard table
 * @prop {int} virtualTableHeight - Height of the virtual table in pixels, also used to calculate whether
 *                                  to use a virtual table or standard table
 * @prop {string|int} virtualTableWidth - Applised this width to the virtual table, either an int (pixels)
 *                                        or string (for a percentage or some other unit)
 * @prop {function} flattenFunction - When table data is provided as an object (typically for grouping data),
 *                                    this function will be applied after grouped data is sorted to
 *                                    provide a flat array for rendering the table rows
 * * headerText {object} - Text to be displayed in the column header
 * * * title {string} - Column header (TH) text
 * * * srOnly {bool} - Determines if the Header text contains the class of "sr-only"
 * * headerAttributes (optional) - key/value pairs of your TH attributes, passed in exactly as you want them written
 * * sortFunction (optional) {function} - Custom sorting function to be used for sorting the data
 * * key {string} - Object key whose value we're using in our sort function
 * * sortDir {string|array} - Default sort order. Values can be "asc", or "desc"
 * * sortColumn {int} - Index (0 based) of the column that is currently active for sorting table data
 * * defaultSort (optional) {bool} - If true, this column will be used to sort the data on page load
 *
 * */

import { createContext, useContext, useRef, forwardRef } from 'react'
import { FixedSizeList } from 'react-window'
import { setSortColumnAndDirection } from '../../actions/approvalRequestsTable'
import SortableTableHeader from './SortableTableHeader'
import {
  sortColumnByKey,
  sortGroupedColumnByKey,
  checkForSameness,
  checkGroupedDataForSameness,
} from '../../utils/sort'
import { isEmpty } from '../../utils/common'
import { usePrevious } from '../../hooks/usePrevious'

const mapStateToProps = state => {
  const {
    approvalRequestsTable: { sortColumn, sortDir },
  } = state
  return {
    sortColumn,
    sortDir,
  }
}

const VirtualTableContext = createContext({
  top: 0,
  setTop: val => {},
  stickyHeader: true,
  header: <></>,
})

const VirtualTable = ({
  row,
  header,
  tableAttributes,
  tableCaption,
  selectedItemIndex,
  sortColumn,
  sortDir,
  ...rest
}) => {
  const listRef = useRef(null)
  const [top, setTop] = useState(0)
  const previousSelectedItemIndex = usePrevious(selectedItemIndex)

  useEffect(() => {
    if (selectedItemIndex) {
      listRef.current.scrollToItem(selectedItemIndex, 'smart')
      // Don't scroll to index 0 when deselecting a row
    } else if (previousSelectedItemIndex === null) {
      listRef.current.scrollToItem(0, 'start')
    }
  }, [selectedItemIndex, sortColumn, sortDir])

  const Inner = forwardRef(({ children, ...rest }, ref) => {
    const { header, top, stickyHeader } = useContext(VirtualTableContext)
    return (
      <div {...rest} ref={ref}>
        <table style={{ top, position: 'absolute', width: '100%' }} {...tableAttributes}>
          {tableCaption && (
            <caption className={tableCaption.srOnly ? 'sr-only' : ''}>
              {tableCaption.caption}
            </caption>
          )}
          {header}
          <tbody>{children}</tbody>
        </table>
      </div>
    )
  })

  return (
    <VirtualTableContext.Provider value={{ top, setTop, header }}>
      <FixedSizeList
        {...rest}
        innerElementType={Inner}
        onItemsRendered={props => {
          const style = listRef.current && listRef.current._getItemStyle(props.overscanStartIndex)
          setTop((style && style.top) || 0)
          rest.onItemsRendered && rest.onItemsRendered(props)
        }}
        ref={listRef}
      >
        {row}
      </FixedSizeList>
    </VirtualTableContext.Provider>
  )
}

const SortableTable = ({
  tableAttributes,
  columns,
  tableCaption,
  virtualRow, // for VirtualTable
  StandardRow,
  hiddenColumns,
  data,
  isGrouped = false,
  itemSize,
  virtualKey,
  standardKey,
  virtualTableHeight = 1000, // in pixels
  virtualTableWidth = '100%',
  flattenFunction,
  overrideVirtual = false,
  selectedItem,
  searchIndexFunc,
  sortColumn,
  sortDir,
  setSortColumnAndDirection,
}) => {
  const [sortedData, setSortedData] = useState([]) // the sorted data that actually gets rendered
  const [selectedItemIndex, setSelectedItemIndex] = useState(0)
  // Use a virtual table if the table elements exceeds the height of the
  // virtual table +1 for the table header.
  const virtual = overrideVirtual || (sortedData.length + 1) * itemSize > virtualTableHeight

  // Set default sort, should only happen when table is initially rendered
  useEffect(() => {
    columns.forEach((column, i) => {
      if (column.defaultSort) {
        setSortColumnAndDirection({ column: i, direction: column.sortDir })
      }
    })
  }, [])

  // Sort data whenever sortColumn, sortDir, or isGrouped changes
  useEffect(() => {
    // Because it'll take a render or two before sortColumn is updated for the current table
    if (!isEmpty(columns) && sortColumn <= columns?.length - 1 && Number.isInteger(sortColumn)) {
      const { key } = columns[sortColumn]
      const sortDirLength = key?.length // limit length of sort directions to length of keys array
      let processedData = []

      if (
        columns[sortColumn].sortFunction &&
        typeof columns[sortColumn].sortFunction === 'function'
      ) {
        processedData = columns[sortColumn].sortFunction(data, key, sortDir.slice(0, sortDirLength))
      } else if (!isGrouped) {
        processedData = checkForSameness(data, columns[sortColumn].key)
          ? data
          : sortColumnByKey(data, key, sortDir.slice(0, sortDirLength))
      } else {
        processedData = flattenFunction(
          checkGroupedDataForSameness(data, columns[sortColumn].key)
            ? data
            : sortGroupedColumnByKey(data, key, sortDir.slice(0, sortDirLength))
        )
      }
      setSortedData(processedData)
      setSelectedItemIndex(
        selectedItem && searchIndexFunc ? processedData.findIndex(searchIndexFunc) : null
      )
    }
  }, [sortColumn, sortDir, isGrouped, columns, data])

  if (virtual) {
    return (
      <VirtualTable
        height={virtualTableHeight}
        width={virtualTableWidth}
        itemCount={sortedData.length}
        itemSize={itemSize}
        itemKey={virtualKey}
        itemData={sortedData}
        header={
          <SortableTableHeader
            columns={columns}
            hiddenColumns={hiddenColumns}
            style={{
              top: 0,
              left: 0,
              width: '100%',
              height: itemSize,
              position: 'sticky',
              zIndex: 1000,
            }}
          />
        }
        row={virtualRow}
        tableAttributes={tableAttributes}
        tableCaption={tableCaption}
        overscanCount={5}
        initialScrollOffset={0}
        selectedItemIndex={selectedItemIndex}
        sortColumn={sortColumn}
        sortDir={sortDir}
      />
    )
  }

  return (
    <table {...tableAttributes}>
      {tableCaption && (
        <caption className={tableCaption.srOnly ? 'sr-only' : ''}>{tableCaption.caption}</caption>
      )}
      <SortableTableHeader columns={columns} hiddenColumns={hiddenColumns} />
      <tbody>
        {sortedData.map((item, index) => (
          <StandardRow data={{ ...item }} index={index} key={standardKey(item)} />
        ))}
      </tbody>
    </table>
  )
}

export default connect(mapStateToProps, { setSortColumnAndDirection })(SortableTable)
